import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import { Link, useLocation } from 'react-router-dom';

interface NavLink {
  id: string;
  label: string;
  href: string;
  icon: React.ReactNode;
}

interface DynamicNavigationProps {
  links: NavLink[];
  theme?: 'light' | 'dark';
  glowIntensity?: number;
  onLinkClick?: (id: string) => void;
  className?: string;
  cta?: React.ReactNode;
}

export const DynamicNavigation = ({
  links,
  theme = 'dark',
  glowIntensity = 5,
  onLinkClick,
  className,
  cta
}: DynamicNavigationProps) => {
  const location = useLocation();
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  return (
    <div 
      className={cn(
        "relative transition-all duration-300 max-w-full",
        className
      )}
    >
      <nav 
        className={cn(
          "flex items-center gap-1.5 md:gap-2 lg:gap-2 p-2 md:p-2 lg:p-2.5 rounded-full border transition-all duration-500 max-w-full",
          theme === 'dark' 
            ? "bg-white/10 border-border backdrop-blur-xl shadow-[0_8px_32px_rgba(0,0,0,0.3)]" 
            : "bg-white/40 border-white/40 backdrop-blur-xl shadow-[0_8px_32px_rgba(0,0,0,0.1)]"
        )}
      >
        {links.map((link) => {
          const isActive = location.pathname === link.href;
          const isHovered = hoveredId === link.id;

          return (
            <Link
              key={link.id}
              to={link.href}
              onMouseEnter={() => setHoveredId(link.id)}
              onMouseLeave={() => setHoveredId(null)}
              onClick={() => onLinkClick?.(link.id)}
              className={cn(
                "relative flex items-center gap-2 lg:gap-2.5 px-3.5 md:px-4 lg:px-5 py-2 md:py-2 lg:py-2.5 rounded-full transition-all duration-300",
                isActive 
                  ? (theme === 'dark' ? "text-accent" : "text-amber-600") 
                  : "text-slate-600",
                isHovered && !isActive && (theme === 'dark' ? "text-foreground" : "text-slate-900")
              )}
            >
              {/* Background Glow/Highlight */}
              <AnimatePresence>
                {(isActive || isHovered) && (
                  <motion.div
                    layoutId="nav-bg"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ 
                      opacity: isActive ? 1 : 0.4, 
                      scale: 1,
                      boxShadow: isActive ? `0 0 ${glowIntensity * 4}px rgba(245, 158, 11, 0.3)` : 'none'
                    }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ type: "spring", stiffness: 400, damping: 30 }}
                    className={cn(
                      "absolute inset-0 rounded-full -z-10",
                      isActive 
                        ? (theme === 'dark' ? "bg-amber-400/10 border border-amber-400/20" : "bg-amber-100 border border-amber-200")
                        : (theme === 'dark' ? "bg-slate-800" : "bg-slate-100")
                    )}
                  />
                )}
              </AnimatePresence>

              <span className="relative z-10 w-5 h-5 flex items-center justify-center">
                {link.icon}
              </span>
              
              <span className="relative z-10 whitespace-nowrap text-xs lg:text-sm font-bold tracking-tight">
                {link.label}
              </span>
            </Link>
          );
        })}

        {cta && (
          <div
            className={cn(
              "ml-1 md:ml-1.5 lg:ml-2 pl-1 md:pl-1.5 lg:pl-2 border-l flex items-center",
              theme === 'dark' ? "border-border" : "border-slate-200/40"
            )}
          >
            {cta}
          </div>
        )}
      </nav>
    </div>
  );
};
